/**
 * Chart Components
 * 
 * Reusable chart components for data visualization
 * 
 * Version: 6.2.0 (Fixed CSS variable color handling)
 */
import React, { useRef, useEffect } from 'react'
import './Charts.css'

// Helper to convert CSS variable to actual color
function resolveColor(color: string): string {
  if (color.startsWith('var(--')) {
    const varName = color.slice(4, -1) // Remove 'var(' and ')'
    const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
    return value || '#6366f1' // Fallback to primary color
  }
  return color
}

// Helper to add alpha to a color
function addAlpha(color: string, alpha: string): string {
  const resolved = resolveColor(color)
  // If already has alpha or is hex with 6+ chars, append alpha
  if (resolved.startsWith('rgba') || resolved.startsWith('rgb')) {
    return resolved
  }
  // For hex colors, convert to rgba
  if (resolved.startsWith('#')) {
    let hex = resolved.slice(1)
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('')
    }
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${parseInt(alpha, 16) / 255})`
  }
  return resolved
}

interface ChartDataPoint {
  label: string
  value: number
  color?: string
}

interface LineChartProps {
  data: { label: string; value: number }[]
  height?: number
  showGrid?: boolean
  showDots?: boolean
  showArea?: boolean
  color?: string
  gradient?: boolean
  className?: string
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 200,
  showGrid = true,
  showDots = true,
  showArea = true,
  color = 'var(--primary)',
  gradient = true,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || data.length === 0) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size for retina
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    const width = rect.width
    const chartHeight = height - 40
    const padding = { top: 20, right: 20, bottom: 30, left: 50 }
    const chartWidth = width - padding.left - padding.right
    const chartAreaHeight = chartHeight - padding.top - padding.bottom

    // Calculate min/max
    const values = data.map(d => d.value)
    const min = Math.min(...values) * 0.9
    const max = Math.max(...values) * 1.1
    const range = max - min || 1

    // Clear canvas
    ctx.clearRect(0, 0, width, height)

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = 'var(--border-color)'
      ctx.lineWidth = 1
      ctx.setLineDash([5, 5])
      
      for (let i = 0; i <= 4; i++) {
        const y = padding.top + (chartAreaHeight / 4) * i
        ctx.beginPath()
        ctx.moveTo(padding.left, y)
        ctx.lineTo(width - padding.right, y)
        ctx.stroke()
      }
      ctx.setLineDash([])
    }

    // Calculate points
    const points = data.map((d, i) => ({
      x: padding.left + (chartWidth / (data.length - 1 || 1)) * i,
      y: padding.top + chartAreaHeight - ((d.value - min) / range) * chartAreaHeight
    }))

    // Draw area gradient
    if (showArea) {
      const areaGradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartAreaHeight)
      if (gradient) {
        areaGradient.addColorStop(0, addAlpha(color, '40'))
        areaGradient.addColorStop(1, addAlpha(color, '00'))
      } else {
        areaGradient.addColorStop(0, addAlpha(color, '20'))
        areaGradient.addColorStop(1, addAlpha(color, '00'))
      }

      ctx.beginPath()
      ctx.moveTo(points[0].x, padding.top + chartAreaHeight)
      points.forEach(p => ctx.lineTo(p.x, p.y))
      ctx.lineTo(points[points.length - 1].x, padding.top + chartAreaHeight)
      ctx.closePath()
      ctx.fillStyle = areaGradient
      ctx.fill()
    }

    // Draw line
    ctx.beginPath()
    ctx.strokeStyle = color
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    
    points.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y)
      else ctx.lineTo(p.x, p.y)
    })
    ctx.stroke()

    // Draw dots
    if (showDots) {
      points.forEach(p => {
        ctx.beginPath()
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2)
        ctx.fillStyle = 'var(--bg-card)'
        ctx.fill()
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.stroke()
      })
    }

    // Draw labels
    ctx.fillStyle = 'var(--text-muted)'
    ctx.font = '11px var(--font-sans)'
    ctx.textAlign = 'center'
    
    data.forEach((d, i) => {
      if (data.length <= 7 || i % Math.ceil(data.length / 7) === 0) {
        ctx.fillText(d.label, points[i].x, height - 8)
      }
    })
  }, [data, height, showGrid, showDots, showArea, color, gradient])

  return (
    <div className={`chart-container ${className}`} style={{ height }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  )
}

interface BarChartProps {
  data: ChartDataPoint[]
  height?: number
  showValues?: boolean
  horizontal?: boolean
  className?: string
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 200,
  showValues = true,
  horizontal = false,
  className = ''
}) => {
  const maxValue = Math.max(...data.map(d => d.value))

  if (horizontal) {
    return (
      <div className={`bar-chart horizontal ${className}`} style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="bar-item">
            <span className="bar-label">{d.label}</span>
            <div className="bar-track">
              <div 
                className="bar-fill" 
                style={{ 
                  width: `${(d.value / maxValue) * 100}%`,
                  backgroundColor: d.color || 'var(--primary)'
                }} 
              />
            </div>
            {showValues && <span className="bar-value">{d.value}</span>}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={`bar-chart ${className}`} style={{ height }}>
      <div className="bar-bars">
        {data.map((d, i) => (
          <div key={i} className="bar-column" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className="bar-column-track">
              <div 
                className="bar-column-fill" 
                style={{ 
                  height: `${(d.value / maxValue) * 100}%`,
                  backgroundColor: d.color || 'var(--primary)'
                }} 
              />
            </div>
            {showValues && <span className="bar-column-value">{d.value}</span>}
            <span className="bar-column-label">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface PieChartProps {
  data: ChartDataPoint[]
  size?: number
  showLegend?: boolean
  className?: string
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  size = 200,
  showLegend = true,
  className = ''
}) => {
  const total = data.reduce((sum, d) => sum + d.value, 0)
  const colors = ['var(--primary)', 'var(--success)', 'var(--warning)', 'var(--danger)', 'var(--info)']

  let currentAngle = -Math.PI / 2

  const slices = data.map((d, i) => {
    const angle = (d.value / total) * Math.PI * 2
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    currentAngle = endAngle

    return {
      ...d,
      color: d.color || colors[i % colors.length],
      percentage: ((d.value / total) * 100).toFixed(1),
      startAngle,
      endAngle
    }
  })

  const createArcPath = (startAngle: number, endAngle: number, radius: number) => {
    const x1 = Math.cos(startAngle) * radius
    const y1 = Math.sin(startAngle) * radius
    const x2 = Math.cos(endAngle) * radius
    const y2 = Math.sin(endAngle) * radius
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0
    
    return `M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`
  }

  return (
    <div className={`pie-chart-container ${className}`}>
      <svg width={size} height={size} viewBox="-100 -100 200 200">
        {slices.map((slice, i) => (
          <path
            key={i}
            d={createArcPath(slice.startAngle, slice.endAngle, 80)}
            fill={slice.color}
            className="pie-slice"
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
        {/* Center circle for donut effect */}
        <circle r="50" fill="var(--bg-card)" />
      </svg>
      
      {showLegend && (
        <div className="pie-legend">
          {slices.map((slice, i) => (
            <div key={i} className="legend-item">
              <span className="legend-color" style={{ backgroundColor: slice.color }} />
              <span className="legend-label">{slice.label}</span>
              <span className="legend-value">{slice.percentage}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default LineChart